import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useCellar } from '@/hooks/useCellar';
import { useRecentTastings } from '@/hooks/useRecentTastings';
import WineCard from '@/components/wine/WineCard';
import DrinkWindowBadge from '@/components/cellar/DrinkWindowBadge';
import Button from '@/components/ui/Button';

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { items: cellarItems, loading: cellarLoading } = useCellar();
  const { tastings, loading: tastingsLoading } = useRecentTastings(4);

  const drinkSoon = cellarItems.filter(
    (item) => item.drinkBy && new Date(item.drinkBy) <= new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
  );

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.signInDetails?.loginId?.split('@')[0] ?? 'there';

  return (
    <div className="page page-home">
      <header className="home-header">
        <div>
          <h1 className="home-app-name">MySommly</h1>
          <p className="home-greeting">{greeting}, {firstName}</p>
          {!cellarLoading && (
            <p className="home-stat">
              {cellarItems.length > 0
                ? `${cellarItems.reduce((s, i) => s + i.quantity, 0)} bottles in cellar`
                : 'Your cellar is empty'}
            </p>
          )}
        </div>
        <button className="home-avatar" onClick={() => navigate('/profile')} aria-label="Profile">
          {firstName.charAt(0).toUpperCase()}
        </button>
      </header>

      <Button variant="primary" fullWidth onClick={() => navigate('/add')} className="home-cta">
        + Log a wine
      </Button>

      {tastings.length > 0 && (
        <section className="home-section">
          <h2 className="section-title">Recent tastings</h2>
          <div className="wine-card-row">
            {tastings.map((t) => (
              <WineCard key={t.id} wine={t.wine} showRating />
            ))}
          </div>
        </section>
      )}

      {drinkSoon.length > 0 && (
        <section className="home-section">
          <h2 className="section-title">Drink soon</h2>
          <ul className="drink-soon-list">
            {drinkSoon.slice(0, 5).map((item) => (
              <li
                key={item.wineId}
                className="drink-soon-item"
                onClick={() => navigate(`/wine/${item.wineId}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/wine/${item.wineId}`)}
              >
                <span className="drink-soon-name">{item.wine.name}</span>
                <span className="drink-soon-meta">
                  {item.wine.vintage} · {item.quantity} bottle{item.quantity !== 1 ? 's' : ''}
                </span>
                <DrinkWindowBadge drinkFrom={item.drinkFrom} drinkBy={item.drinkBy} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {tastings.length === 0 && !tastingsLoading && (
        <section className="home-empty">
          <p>Start by scanning a wine label or searching for a wine you've had.</p>
          <Button variant="secondary" onClick={() => navigate('/add')}>Add your first wine</Button>
        </section>
      )}
    </div>
  );
}
